import { format, isSameDay } from 'date-fns';
import domPurify from 'dompurify';
import * as React from 'react';

import { EventView, PersonOrgType } from '@ndvr/customer-api';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { convertBackendTimeToBrowser, formatTime, getHouseholdName, useHouseholdRoutePaths } from '../../../utils';
import { PersonOrgLogoCard } from '../../../widgets/LogoCard';
import { useHouseholdMap } from '../../household/household-queries';

export interface EventCalendarEventCardProps {
    event: EventView;
    onOpenDetails?: (event: EventView) => void;
}

export const EventCalendarEventCard = ({ event, onOpenDetails }: EventCalendarEventCardProps) => {
    const { PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const participants = React.useMemo(() => {
        return (event?.participants || []).map((p) => p?.name || p?.email).filter(Boolean);
    }, [event]);

    const householdMap = useHouseholdMap();
    const householdProfile = event.clientHouseholdGUID ? householdMap.get(event.clientHouseholdGUID) : undefined;

    return (
        <Card className="mb-4 last:mb-0">
            <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                    <div className="flex-1">
                        <div className="mb-1 flex items-center gap-5">
                            <span
                                className="cursor-pointer text-lg font-semibold break-all hover:underline"
                                role="button"
                                tabIndex={0}
                                onClick={() => onOpenDetails?.(event)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpenDetails?.(event);
                                    }
                                }}
                            >
                                {event?.title}
                            </span>
                        </div>

                        <div className="text-muted-foreground flex flex-wrap items-center gap-4 text-sm break-all">
                            <span>
                                {(() => {
                                    const start = convertBackendTimeToBrowser(event.startTime, event.startTimeZone);
                                    const end = convertBackendTimeToBrowser(event.endTime, event.endTimeZone);
                                    if (isSameDay(start, end)) {
                                        return `${formatTime(start)} - ${formatTime(end)}  ${format(start, 'MMM d, yyyy')}`;
                                    }
                                    return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
                                })()}
                            </span>

                            {event.url && <span className="basis-full">💻 Virtual</span>}
                        </div>

                        {event.url && (
                            <Button variant="link" asChild className="mt-1 px-0 text-left break-all whitespace-normal">
                                <a href={event.url}>Join Meeting: {event.url}</a>
                            </Button>
                        )}
                    </div>
                </div>

                {householdProfile && (
                    <div className="mt-4 border-t pt-4">
                        <PersonOrgLogoCard
                            name={getHouseholdName(householdProfile) || 'Unnamed Household'}
                            personOrgType={PersonOrgType.ORGANIZATION}
                            to={PortfolioOverviewTabRoutePath(event.clientHouseholdGUID)}
                        />
                    </div>
                )}

                {(event.description || participants.length > 0) && (
                    <Accordion type="multiple" className="mt-4">
                        {event.description && (
                            <AccordionItem value={`details-${event.eventGUID}`}>
                                <AccordionTrigger>Details</AccordionTrigger>
                                <AccordionContent>
                                    <div
                                        className="text-muted-foreground pt-2 text-sm break-all whitespace-pre-wrap select-text"
                                        dangerouslySetInnerHTML={{
                                            __html: domPurify.sanitize(event.description),
                                        }}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                        )}
                        {participants.length > 0 && (
                            <AccordionItem value={`attendees-${event.eventGUID}`}>
                                <AccordionTrigger className="flex max-w-[100%] items-center gap-2">
                                    <span>Participants</span>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ul className="list-inside list-disc space-y-1">
                                        {participants.map((p) => (
                                            <li key={p}>{p}</li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        )}
                    </Accordion>
                )}
            </CardContent>
        </Card>
    );
};

export default EventCalendarEventCard;
